<template>
  <div class="system-settings">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>系统设置</h2>
      <p>管理系统配置、权限设置和系统信息</p>
    </div>

    <!-- 设置标签页 -->
    <el-tabs v-model="activeTab" class="settings-tabs">
      <!-- 基础配置 -->
      <el-tab-pane label="基础配置" name="basic">
        <el-card class="settings-card">
          <template #header>
            <div class="card-header">
              <span>基础配置</span>
              <el-button 
                type="primary" 
                size="small" 
                @click="saveBasicConfigs"
                :loading="saving"
              >
                保存配置
              </el-button>
            </div>
          </template>
          
          <el-form :model="basicConfigs" label-width="150px" class="config-form">
            <el-form-item label="系统名称">
              <el-input 
                v-model="basicConfigs.system_name" 
                placeholder="请输入系统名称"
                maxlength="50"
                show-word-limit
              />
            </el-form-item>
            
            <el-form-item label="JWT过期时间">
              <el-input 
                v-model="basicConfigs.jwt_expires_in" 
                placeholder="如: 7d, 24h, 3600s"
                maxlength="10"
              >
                <template #append>天/小时/秒</template>
              </el-input>
            </el-form-item>
            
            <el-form-item label="验证超时时间">
              <el-input-number 
                v-model="basicConfigs.verify_timeout" 
                :min="10" 
                :max="3600"
                controls-position="right"
              />
              <span class="form-help">秒，建议300秒</span>
            </el-form-item>
            
            <el-form-item label="设备绑定限制">
              <el-input-number 
                v-model="basicConfigs.device_bind_limit" 
                :min="1" 
                :max="100"
                controls-position="right"
              />
              <span class="form-help">每个卡密最多绑定设备数</span>
            </el-form-item>
            
            <el-form-item label="心跳间隔">
              <el-input-number 
                v-model="basicConfigs.heartbeat_interval" 
                :min="60" 
                :max="86400"
                controls-position="right"
              />
              <span class="form-help">秒，建议3600秒</span>
            </el-form-item>
            
            <el-form-item label="最大登录尝试">
              <el-input-number 
                v-model="basicConfigs.max_login_attempts" 
                :min="3" 
                :max="20"
                controls-position="right"
              />
              <span class="form-help">次，超过后锁定账户</span>
            </el-form-item>
            
            <el-form-item label="登录锁定时长">
              <el-input-number 
                v-model="basicConfigs.login_lock_duration" 
                :min="300" 
                :max="86400"
                controls-position="right"
              />
              <span class="form-help">秒，建议1800秒</span>
            </el-form-item>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 日志配置 -->
      <el-tab-pane label="日志配置" name="logs">
        <el-card class="settings-card">
          <template #header>
            <div class="card-header">
              <span>日志配置</span>
              <el-button 
                type="primary" 
                size="small" 
                @click="saveLogConfigs"
                :loading="saving"
              >
                保存配置
              </el-button>
            </div>
          </template>
          
          <el-form :model="logConfigs" label-width="150px" class="config-form">
            <el-form-item label="启用操作日志">
              <el-switch 
                v-model="logConfigs.enable_operation_log"
                active-text="开启"
                inactive-text="关闭"
              />
              <span class="form-help">记录管理员操作日志</span>
            </el-form-item>
            
            <el-form-item label="启用验证日志">
              <el-switch 
                v-model="logConfigs.enable_verify_log"
                active-text="开启"
                inactive-text="关闭"
              />
              <span class="form-help">记录卡密验证日志</span>
            </el-form-item>
            
            <el-form-item label="日志保留天数">
              <el-input-number 
                v-model="logConfigs.log_retention_days" 
                :min="7" 
                :max="365"
                controls-position="right"
              />
              <span class="form-help">天，超过后自动清理</span>
            </el-form-item>
            
            <el-form-item label="日志级别">
              <el-select v-model="logConfigs.log_level" placeholder="请选择日志级别">
                <el-option label="错误 (Error)" value="error" />
                <el-option label="警告 (Warn)" value="warn" />
                <el-option label="信息 (Info)" value="info" />
                <el-option label="调试 (Debug)" value="debug" />
              </el-select>
              <span class="form-help">日志记录级别</span>
            </el-form-item>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 权限配置 -->
      <el-tab-pane label="权限配置" name="permissions">
        <el-card class="settings-card">
          <template #header>
            <div class="card-header">
              <span>权限配置</span>
              <el-button 
                type="primary" 
                size="small" 
                @click="refreshPermissions"
                :loading="loadingPermissions"
              >
                刷新权限
              </el-button>
            </div>
          </template>
          
          <div v-loading="loadingPermissions">
            <div v-for="cardType in cardTypes" :key="cardType.value" class="permission-group">
              <h4 class="permission-title">{{ cardType.label }}</h4>
              <el-row :gutter="20">
                <el-col
                  :span="8"
                  v-for="permission in getPermissionsByCardType(cardType.value)"
                  :key="permission.permission_key"
                  class="permission-item"
                >
                  <el-card shadow="hover" class="permission-card">
                    <div class="permission-content">
                      <div class="permission-info">
                        <h5>{{ permission.permission_name }}</h5>
                        <p>{{ permission.description }}</p>
                      </div>
                      <el-switch
                        v-model="permission.enabled"
                        @change="updatePermission(cardType.value, permission.permission_key, permission.enabled)"
                        active-text="启用"
                        inactive-text="禁用"
                      />
                    </div>
                  </el-card>
                </el-col>
              </el-row>
            </div>
          </div>
        </el-card>
      </el-tab-pane>

      <!-- 系统信息 -->
      <el-tab-pane label="系统信息" name="system">
        <el-card class="settings-card">
          <template #header>
            <div class="card-header">
              <span>系统信息</span>
              <el-button 
                type="primary" 
                size="small" 
                @click="refreshSystemInfo"
                :loading="loadingSystemInfo"
              >
                刷新信息
              </el-button>
            </div>
          </template>
          
          <div v-loading="loadingSystemInfo">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="系统版本">
                {{ systemInfo.version }}
              </el-descriptions-item>
              <el-descriptions-item label="Node.js版本">
                {{ systemInfo.node_version }}
              </el-descriptions-item>
              <el-descriptions-item label="运行平台">
                {{ systemInfo.platform }} ({{ systemInfo.arch }})
              </el-descriptions-item>
              <el-descriptions-item label="运行环境">
                <el-tag :type="systemInfo.env === 'production' ? 'success' : 'warning'">
                  {{ systemInfo.env }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="运行时间">
                {{ formatUptime(systemInfo.uptime) }}
              </el-descriptions-item>
              <el-descriptions-item label="服务器时间">
                {{ formatDateTime(systemInfo.server_time) }}
              </el-descriptions-item>
            </el-descriptions>
            
            <el-divider content-position="left">内存使用情况</el-divider>
            <el-row :gutter="20" class="memory-stats">
              <el-col :span="6">
                <el-statistic title="RSS内存" :value="formatBytes(systemInfo.memory_usage?.rss)" />
              </el-col>
              <el-col :span="6">
                <el-statistic title="堆内存总量" :value="formatBytes(systemInfo.memory_usage?.heapTotal)" />
              </el-col>
              <el-col :span="6">
                <el-statistic title="堆内存使用" :value="formatBytes(systemInfo.memory_usage?.heapUsed)" />
              </el-col>
              <el-col :span="6">
                <el-statistic title="外部内存" :value="formatBytes(systemInfo.memory_usage?.external)" />
              </el-col>
            </el-row>
            
            <el-divider content-position="left">数据库统计</el-divider>
            <el-row :gutter="20" class="db-stats">
              <el-col :span="4">
                <el-statistic title="管理员数量" :value="systemInfo.admin_count" />
              </el-col>
              <el-col :span="4">
                <el-statistic title="代理商数量" :value="systemInfo.agent_count" />
              </el-col>
              <el-col :span="4">
                <el-statistic title="卡密数量" :value="systemInfo.card_count" />
              </el-col>
              <el-col :span="4">
                <el-statistic title="设备数量" :value="systemInfo.device_count" />
              </el-col>
              <el-col :span="4">
                <el-statistic title="验证日志" :value="systemInfo.verify_log_count" />
              </el-col>
              <el-col :span="4">
                <el-statistic title="操作日志" :value="systemInfo.operation_log_count" />
              </el-col>
            </el-row>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { configAPI } from '@/services/api'

export default {
  name: 'SystemSettings',
  setup() {
    const activeTab = ref('basic')
    const saving = ref(false)
    const loadingPermissions = ref(false)
    const loadingSystemInfo = ref(false)

    // 基础配置
    const basicConfigs = reactive({
      system_name: '',
      jwt_expires_in: '',
      verify_timeout: 300,
      device_bind_limit: 1,
      heartbeat_interval: 3600,
      max_login_attempts: 5,
      login_lock_duration: 1800
    })

    // 日志配置
    const logConfigs = reactive({
      enable_operation_log: true,
      enable_verify_log: true,
      log_retention_days: 30,
      log_level: 'info'
    })

    // 权限配置
    const permissions = ref([])
    const cardTypes = ref([
      { label: 'AI语音基础版', value: 'AI_HOST_BASIC' },
      { label: 'AI语音专业版', value: 'AI_HOST_PRO' },
      { label: 'AI盒子基础版', value: 'AI_BOX_BASIC' },
      { label: 'AI盒子专业版', value: 'AI_BOX_PRO' },
      { label: '全功能版', value: 'ALL_IN_ONE' }
    ])

    // 系统信息
    const systemInfo = reactive({
      version: '',
      node_version: '',
      platform: '',
      arch: '',
      uptime: 0,
      memory_usage: {},
      env: '',
      server_time: '',
      admin_count: 0,
      agent_count: 0,
      card_count: 0,
      device_count: 0,
      verify_log_count: 0,
      operation_log_count: 0
    })

    // 获取系统配置
    const loadConfigs = async () => {
      try {
        const response = await configAPI.getConfigs()
        if (response.data.success) {
          const configs = response.data.data
          
          // 填充基础配置
          basicConfigs.system_name = configs.system_name?.value || 'FanqianAI验证系统'
          basicConfigs.jwt_expires_in = configs.jwt_expires_in?.value || '7d'
          basicConfigs.verify_timeout = parseInt(configs.verify_timeout?.value) || 300
          basicConfigs.device_bind_limit = parseInt(configs.device_bind_limit?.value) || 1
          basicConfigs.heartbeat_interval = parseInt(configs.heartbeat_interval?.value) || 3600
          basicConfigs.max_login_attempts = parseInt(configs.max_login_attempts?.value) || 5
          basicConfigs.login_lock_duration = parseInt(configs.login_lock_duration?.value) || 1800
          
          // 填充日志配置
          logConfigs.enable_operation_log = configs.enable_operation_log?.value === 'true'
          logConfigs.enable_verify_log = configs.enable_verify_log?.value === 'true'
          logConfigs.log_retention_days = parseInt(configs.log_retention_days?.value) || 30
          logConfigs.log_level = configs.log_level?.value || 'info'
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        ElMessage.error('加载配置失败')
      }
    }

    // 保存基础配置
    const saveBasicConfigs = async () => {
      saving.value = true
      try {
        const configs = {
          system_name: basicConfigs.system_name,
          jwt_expires_in: basicConfigs.jwt_expires_in,
          verify_timeout: basicConfigs.verify_timeout.toString(),
          device_bind_limit: basicConfigs.device_bind_limit.toString(),
          heartbeat_interval: basicConfigs.heartbeat_interval.toString(),
          max_login_attempts: basicConfigs.max_login_attempts.toString(),
          login_lock_duration: basicConfigs.login_lock_duration.toString()
        }
        
        const response = await configAPI.updateConfigs({ configs })
        if (response.data.success) {
          ElMessage.success('基础配置保存成功')
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存配置失败:', error)
        ElMessage.error('保存配置失败')
      } finally {
        saving.value = false
      }
    }

    // 保存日志配置
    const saveLogConfigs = async () => {
      saving.value = true
      try {
        const configs = {
          enable_operation_log: logConfigs.enable_operation_log.toString(),
          enable_verify_log: logConfigs.enable_verify_log.toString(),
          log_retention_days: logConfigs.log_retention_days.toString(),
          log_level: logConfigs.log_level
        }
        
        const response = await configAPI.updateConfigs({ configs })
        if (response.data.success) {
          ElMessage.success('日志配置保存成功')
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存配置失败:', error)
        ElMessage.error('保存配置失败')
      } finally {
        saving.value = false
      }
    }

    // 获取权限配置
    const loadPermissions = async () => {
      loadingPermissions.value = true
      try {
        const response = await configAPI.getPermissions()
        if (response.data.success) {
          permissions.value = response.data.data
        }
      } catch (error) {
        console.error('加载权限失败:', error)
        ElMessage.error('加载权限失败')
      } finally {
        loadingPermissions.value = false
      }
    }

    // 根据卡密类型获取权限
    const getPermissionsByCardType = (cardType) => {
      return permissions.value[cardType] || []
    }

    // 更新权限
    const updatePermission = async (cardType, permissionKey, enabled) => {
      try {
        const response = await configAPI.updatePermission(cardType, permissionKey, { enabled })
        if (response.data.success) {
          ElMessage.success('权限更新成功')
        } else {
          ElMessage.error(response.data.message || '更新失败')
        }
      } catch (error) {
        console.error('更新权限失败:', error)
        ElMessage.error('更新权限失败')
      }
    }

    // 刷新权限
    const refreshPermissions = () => {
      loadPermissions()
    }

    // 获取系统信息
    const loadSystemInfo = async () => {
      loadingSystemInfo.value = true
      try {
        const response = await configAPI.getSystemInfo()
        if (response.data.success) {
          Object.assign(systemInfo, response.data.data)
        }
      } catch (error) {
        console.error('加载系统信息失败:', error)
        ElMessage.error('加载系统信息失败')
      } finally {
        loadingSystemInfo.value = false
      }
    }

    // 刷新系统信息
    const refreshSystemInfo = () => {
      loadSystemInfo()
    }

    // 格式化运行时间
    const formatUptime = (seconds) => {
      if (!seconds) return '0秒'

      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)

      let result = ''
      if (days > 0) result += `${days}天`
      if (hours > 0) result += `${hours}小时`
      if (minutes > 0) result += `${minutes}分钟`
      if (secs > 0 || result === '') result += `${secs}秒`

      return result
    }

    // 格式化字节数
    const formatBytes = (bytes) => {
      if (!bytes) return '0 B'

      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))

      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    }

    // 格式化日期时间
    const formatDateTime = (dateTime) => {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN')
    }

    // 页面加载时获取数据
    onMounted(() => {
      loadConfigs()
      loadPermissions()
      loadSystemInfo()
    })

    return {
      activeTab,
      saving,
      loadingPermissions,
      loadingSystemInfo,
      basicConfigs,
      logConfigs,
      permissions,
      cardTypes,
      systemInfo,
      loadConfigs,
      saveBasicConfigs,
      saveLogConfigs,
      loadPermissions,
      getPermissionsByCardType,
      updatePermission,
      refreshPermissions,
      loadSystemInfo,
      refreshSystemInfo,
      formatUptime,
      formatBytes,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.system-settings {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
}

.page-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.settings-tabs {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.settings-card {
  border: none;
  box-shadow: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.config-form {
  max-width: 600px;
}

.form-help {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.permission-group {
  margin-bottom: 30px;
}

.permission-title {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #409EFF;
  padding-bottom: 8px;
}

.permission-item {
  margin-bottom: 15px;
}

.permission-card {
  height: 100%;
}

.permission-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.permission-info h5 {
  margin: 0 0 5px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.permission-info p {
  margin: 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.memory-stats,
.db-stats {
  margin-top: 20px;
}

.memory-stats .el-col,
.db-stats .el-col {
  margin-bottom: 20px;
}

:deep(.el-tabs__header) {
  margin: 0;
  padding: 0 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

:deep(.el-tabs__content) {
  padding: 20px;
}

:deep(.el-descriptions__label) {
  font-weight: 600;
}

:deep(.el-statistic__head) {
  font-size: 14px;
  color: #909399;
}

:deep(.el-statistic__content) {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
</style>
